import { createTranslation } from '@choffmeister/i18next'
import { css, SerializedStyles } from '@emotion/core'
import parse from 'html-react-parser'
import * as React from 'react'

import { adGalleryBreakPoint_Fr } from '../../constants/breakPoints'
import { breakpoints } from '../../constants/breakPoints'
import { adGalleryContent, AdGalleryContentProps } from '../../constants/newAdPages'
import { isElementInView } from '../../helpers/isElementInView'
import { useLanguage } from '../../hooks/useLanguage'
import Grid from '../Grid'
import Image from '../Image'
import Video from '../Video'
import ViewsDropdown from '../ViewsDropdown'

interface AdGalleryProps {
  heading?: string
  galleryContent?: AdGalleryContentProps[]
  tabsContainerBreakPoint?: number
  cssStyles?: SerializedStyles
}

const AdGallery: React.FunctionComponent<AdGalleryProps> = ({
  heading,
  galleryContent = adGalleryContent,
  tabsContainerBreakPoint,
  cssStyles,
}) => {
  const [currentItem, setCurrentItem] = React.useState(0)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const [inView, setInView] = React.useState(false)
  React.useEffect(() => {
    const scrollHandler = () => {
      if (!inView && containerRef.current && isElementInView(containerRef.current)) {
        setInView(true)
      }
    }

    document.addEventListener('scroll', scrollHandler)

    return () => {
      document.removeEventListener('scroll', scrollHandler)
    }
  }, [inView])

  const { useTranslation } = createTranslation({
    galleryHeading: {
      en: 'One central platform for all your product work',
      fr: 'Boostez vos équipes produit de la vision à la livraison',
      de: 'Ein zentraler Ort für deine Produktarbeit',
    },
    galleryContent,
  })
  const { t } = useTranslation()

  const isFrPage = useLanguage() === 'fr'

  return (
    <div css={cssStyles}>
      <h2 className="heading2 center">{heading ? parse(heading) : t('galleryHeading')}</h2>
      <div css={containerStyle}>
        <div
          className="ad-gallery-tabs-container"
          css={tabsContainerStyle(galleryContent.length, tabsContainerBreakPoint)}
        >
          {galleryContent.map(({}, index) => {
            const selectedItem = currentItem === index
            return (
              <div
                key={index}
                className={`ad-gallery-tab strong-body-2 ${!selectedItem ? 'opacity-on-hover' : ''}`}
                onClick={() => setCurrentItem(index)}
                css={selectedItem && currentTabStyle}
              >
                {parse(t(`galleryContent.${index}.name`))}
              </div>
            )
          })}
        </div>
        <ViewsDropdown
          items={galleryContent}
          currentItem={currentItem}
          setCurrentItem={setCurrentItem}
          cssStyles={dropdownStyle(tabsContainerBreakPoint)}
        />
      </div>
      <Grid
        columns={2}
        columnGap={galleryContent[currentItem].imgSrc ? 5 : 3}
        columnsTemplate={galleryContent[currentItem].imgSrc ? '1fr 2.5fr' : '1fr 3fr'}
        breakPoint={isFrPage ? adGalleryBreakPoint_Fr : breakpoints.medium}
        alignContent="flex-start"
      >
        <div className="ad-gallery-content" css={contentStyle(isFrPage)}>
          <h3 className="heading3">{parse(t(`galleryContent.${currentItem}.title`))}</h3>
          <div className="body-1">{parse(t(`galleryContent.${currentItem}.description`))}</div>
        </div>

        {galleryContent[currentItem].mp4VideoSrc && galleryContent[currentItem].webmVideoSrc && (
          <div ref={containerRef} className="gray-bordered-block">
            <Video
              key={t(`galleryContent.${currentItem}.title`)}
              srcType={[
                { src: galleryContent[currentItem].mp4VideoSrc ?? '', type: 'video/mp4' },
                { src: galleryContent[currentItem].webmVideoSrc ?? '', type: 'video/webm' },
              ]}
              ratio={1.756363636}
              playInView={!inView}
              loop
              loopDelay={2000}
            />
          </div>
        )}

        {galleryContent[currentItem].imgSrc && (
          <Image src={galleryContent[currentItem].imgSrc ?? ''} alt={t(`galleryContent.${currentItem}.name`)} />
        )}
      </Grid>
    </div>
  )
}

export default AdGallery

const containerStyle = css`
  margin-bottom: 4rem;

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin-bottom: 2rem;
  }
`

const tabsContainerStyle = (viewsLength: number, tabsContainerBreakPoint?: number) => css`
  display: flex;
  ${viewsLength > 4 && 'justify-content: space-between'};
  border-bottom: 1px solid #efeff0;
  margin-bottom: 2rem;

  .strong-body-2 {
    padding: 1.5rem 0 0.375rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    border-bottom: 2px solid transparent;

    ${viewsLength < 5 && '&:not(:last-child) {margin-right: 3rem;}'};
  }

  @media screen and (max-width: ${tabsContainerBreakPoint || breakpoints.small}px) {
    display: none;
  }
`

const currentTabStyle = css`
  color: #318eff;
  border-bottom: 2px solid #318eff !important;
`

const dropdownStyle = (tabsContainerBreakPoint?: number) => css`
  margin: 1rem auto;
  max-width: 20rem;

  @media screen and (min-width: ${(tabsContainerBreakPoint || breakpoints.small) + 1}px) {
    display: none;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    max-width: 100%;
  }
`

const contentStyle = (isFrPage: boolean) => css`
  @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${isFrPage
      ? adGalleryBreakPoint_Fr
      : breakpoints.medium}px) {
    text-align: center;
  }
`
